<script setup lang="ts">
/**
 * Settings → Medical questionnaire (§21).
 *
 * Dos bloques, y la separación es el punto de la pantalla: arriba lo que
 * pregunta Blue Trails —el piso común, solo lectura— y debajo lo que agrega el
 * centro. Si el centro pudiera editar el catálogo base, ese piso dejaría de
 * existir y cada cuenta tendría un cuestionario distinto con el mismo nombre.
 *
 * Las preguntas son de la CUENTA y no de un dive center: es la misma decisión
 * que la libreta de clientes, y con la cuenta típica de un solo centro pedir
 * elegir uno antes de editar sería un paso de más.
 */
import {
  createMedicalQuestion,
  deleteMedicalQuestion,
  listMedicalQuestions,
  updateMedicalQuestion,
  updateQuestionnaireSettings,
  type MedicalQuestion,
  type QuestionnaireSettings,
} from '@/api/medical'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import { useAuthStore } from '@/stores/auth'

definePage({ meta: { requiresAuth: true } })

const auth = useAuthStore()

// Editar el catálogo es de owner y admin (el backend lo exige igual): cambiar
// qué se le pregunta a alguien sobre su salud es una decisión del negocio, no
// del turno de mostrador. Los demás ven la pantalla en modo lectura.
const canManage = computed(() => ['owner', 'admin'].includes(auth.providerUser?.role ?? ''))

const loading = ref(true)
const error = ref('')

const blueTrails = ref<MedicalQuestion[]>([])
const settings = ref<QuestionnaireSettings>({ active: false })
const nameDraft = ref('')
const savingSettings = ref(false)

/**
 * Prende o apaga el cuestionario propio.
 *
 * El backend rechaza prenderlo sin preguntas activas: al buzo le aparecería una
 * tarjeta en Documentos que al abrirla no pide nada.
 */
const toggleQuestionnaire = async (value: boolean | null) => {
  savingSettings.value = true
  try {
    const res = await updateQuestionnaireSettings({ active: !!value })

    settings.value = res.settings
    notifySuccess(res.settings.active
      ? 'Your questionnaire is on. Linked divers will see it in the app.'
      : 'Your questionnaire is off.')
  }
  catch (e) {
    settings.value = { ...settings.value }
    notifyError(apiMessage(e, 'Could not change that.'))
  }
  finally {
    savingSettings.value = false
  }
}

const saveName = async () => {
  savingSettings.value = true
  try {
    const res = await updateQuestionnaireSettings({ name: nameDraft.value.trim() })

    settings.value = res.settings
    notifySuccess('Saved.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save the name.'))
  }
  finally {
    savingSettings.value = false
  }
}
const own = ref<MedicalQuestion[]>([])

const showEditor = ref(false)
const editing = ref<MedicalQuestion | null>(null)
const draftText = ref('')
const draftHelp = ref('')
const submitting = ref(false)
const editorError = ref('')

const togglingId = ref<string | null>(null)
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    // Todas, activas e inactivas: esta es la pantalla donde se vuelven a
    // encender, así que esconder las apagadas las dejaría inalcanzables.
    const res = await listMedicalQuestions()

    blueTrails.value = res.blueTrails
    own.value = res.provider
    settings.value = res.settings ?? { active: false }
    nameDraft.value = res.settings?.name ?? ''
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load the questionnaire.'
  }
  finally {
    loading.value = false
  }
}

function startCreate() {
  editing.value = null
  draftText.value = ''
  draftHelp.value = ''
  editorError.value = ''
  showEditor.value = true
}

function startEdit(q: MedicalQuestion) {
  editing.value = q
  draftText.value = q.text
  draftHelp.value = q.helpText ?? ''
  editorError.value = ''
  showEditor.value = true
}

const canSubmitDraft = computed(() => !!draftText.value.trim())

/**
 * Reemplaza una pregunta en la lista con lo que devolvió el backend.
 *
 * Cada endpoint devuelve la pregunta ya guardada, así que recargar el catálogo
 * entero después de cada gesto era un viaje de más — y peor: el redibujado
 * mandaba la página arriba de todo, obligando a bajar de nuevo hasta el
 * cuestionario propio para agregar la siguiente pregunta.
 */
function replaceQuestion(q: MedicalQuestion) {
  const i = own.value.findIndex(x => x.id === q.id)

  if (i === -1)
    own.value.push(q)
  else
    own.value[i] = q
}

async function submitEditor() {
  if (!canSubmitDraft.value)
    return

  editorError.value = ''
  submitting.value = true
  try {
    const payload = {
      labels: draftText.value.trim(),
      // Cadena vacía y no `undefined`: es la señal de "borrá la aclaración".
      // Con undefined el backend lo leería como "no tocar".
      helpLabels: draftHelp.value.trim(),
    }

    const res = editing.value
      ? await updateMedicalQuestion(editing.value.id, payload)
      : await createMedicalQuestion(payload)

    replaceQuestion(res.question)
    notifySuccess(editing.value ? 'Question updated.' : 'Question added.')
    showEditor.value = false
  }
  catch (e) {
    editorError.value = e instanceof ApiError ? e.message : 'Could not save the question.'
  }
  finally {
    submitting.value = false
  }
}

async function toggleActive(q: MedicalQuestion) {
  togglingId.value = q.id
  try {
    const res = await updateMedicalQuestion(q.id, { active: !q.active })

    replaceQuestion(res.question)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not update the question.'))
  }
  finally {
    togglingId.value = null
  }
}

async function remove(q: MedicalQuestion) {
  const ok = await confirmRef.value?.open({
    title: 'Delete question',
    message:
      `Delete “${q.text}”? Declarations already signed keep their own copy of the `
      + `wording, so they stay readable. If you only want to stop asking it, turn it off instead.`,
    confirmText: 'Delete',
    confirmColor: 'error',
  })
  if (!ok)
    return

  try {
    await deleteMedicalQuestion(q.id)
    own.value = own.value.filter(x => x.id !== q.id)
    notifySuccess('Question deleted.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not delete the question.'))
  }
}
</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else>
      <!-- El cuestionario propio va primero: es lo único editable de esta
           pantalla. Debajo de las 10 de Blue Trails, agregar una pregunta
           obligaba a bajar hasta el fondo cada vez. -->
      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>Your own questionnaire</VCardTitle>
          <VCardSubtitle>
            A separate questionnaire, not an addition to the Blue Trails one. Turn it on and
            your linked divers will find it in the app, badged with your dive center.
          </VCardSubtitle>

          <template #append>
            <div class="d-flex align-center gap-3">
              <VSwitch
                :model-value="settings.active"
                :disabled="!canManage || savingSettings"
                :label="settings.active ? 'On' : 'Off'"
                hide-details
                @update:model-value="toggleQuestionnaire"
              />
              <VBtn
                v-if="canManage"
                prepend-icon="ri-add-line"
                @click="startCreate"
              >
                Add question
              </VBtn>
            </div>
          </template>
        </VCardItem>

        <VDivider />

        <VList
          v-if="own.length"
          lines="two"
        >
          <VListItem
            v-for="q in own"
            :key="q.id"
          >
            <VListItemTitle class="text-wrap">
              {{ q.text }}
              <VChip
                v-if="!q.active"
                size="x-small"
                color="secondary"
                class="ms-2"
              >
                Off
              </VChip>
            </VListItemTitle>
            <VListItemSubtitle
              v-if="q.helpText"
              class="text-wrap"
            >
              {{ q.helpText }}
            </VListItemSubtitle>

            <template #append>
              <div class="d-flex align-center ga-1">
                <!-- Apagar en vez de borrar: deja de preguntarse sin perder el
                     registro de lo que ya se contestó. -->
                <VSwitch
                  :model-value="q.active"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!canManage || togglingId === q.id"
                  :aria-label="q.active ? 'Stop asking this question' : 'Ask this question again'"
                  @update:model-value="toggleActive(q)"
                />
                <IconBtn
                  v-if="canManage"
                  size="small"
                  title="Edit question"
                  aria-label="Edit question"
                  @click="startEdit(q)"
                >
                  <VIcon icon="ri-edit-box-line" />
                </IconBtn>
                <IconBtn
                  v-if="canManage"
                  size="small"
                  title="Delete question"
                  aria-label="Delete question"
                  @click="remove(q)"
                >
                  <VIcon icon="ri-delete-bin-7-line" />
                </IconBtn>
              </div>
            </template>
          </VListItem>
        </VList>

        <VCardText
          v-else
          class="text-center py-8"
        >
          <p class="text-body-1 mb-4">
            Nothing of your own yet. Add what your center needs to know beyond the baseline —
            local rules, the conditions of a specific site, your insurer's requirements.
          </p>
          <VBtn
            v-if="canManage"
            prepend-icon="ri-add-line"
            @click="startCreate"
          >
            Add your first question
          </VBtn>
        </VCardText>
      </VCard>

      <VCard>
        <VCardItem>
          <VCardTitle>Blue Trails questions</VCardTitle>
          <VCardSubtitle>
            The baseline every dive center on Blue Trails asks. Read-only.
          </VCardSubtitle>
        </VCardItem>

        <VDivider />

        <VList lines="two">
          <VListItem
            v-for="(q, i) in blueTrails"
            :key="q.id"
          >
            <template #prepend>
              <span class="text-medium-emphasis me-4">{{ i + 1 }}</span>
            </template>

            <VListItemTitle class="text-wrap">
              {{ q.text }}
            </VListItemTitle>
            <VListItemSubtitle
              v-if="q.helpText"
              class="text-wrap"
            >
              {{ q.helpText }}
            </VListItemSubtitle>
          </VListItem>
        </VList>

        <VDivider />

        <VCardText class="text-body-2 text-medium-emphasis">
          A “yes” does not disqualify anyone — it means that diver should be cleared by a
          doctor before getting in the water. This questionnaire is a screening tool, not a
          medical assessment.
        </VCardText>
      </VCard>
    </template>

    <VDialog
      v-model="showEditor"
      max-width="560"
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between pe-2">
          <span>{{ editing ? 'Edit question' : 'Add question' }}</span>
          <VBtn
            icon="ri-close-line"
            variant="text"
            size="small"
            @click="showEditor = false"
          />
        </VCardTitle>
        <VDivider />

        <VForm @submit.prevent="submitEditor">
          <VCardText>
            <AlertBox
              variant="danger"
              :message="editorError"
            />

            <VTextarea
              v-model="draftText"
              label="Question *"
              rows="2"
              auto-grow
              autofocus
              hint="It has to be answerable with yes or no."
              persistent-hint
              :disabled="submitting"
            />

            <VTextarea
              v-model="draftHelp"
              class="mt-6"
              label="Clarification"
              rows="2"
              auto-grow
              hint="Optional. Shown in smaller print under the question."
              persistent-hint
              :disabled="submitting"
            />
          </VCardText>

          <VDivider />
          <VCardActions class="px-4 py-3">
            <VSpacer />
            <VBtn
              variant="text"
              :disabled="submitting"
              @click="showEditor = false"
            >
              Cancel
            </VBtn>
            <VBtn
              type="submit"
              color="primary"
              :loading="submitting"
              :disabled="!canSubmitDraft"
            >
              {{ editing ? 'Save changes' : 'Add question' }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <ConfirmDialog ref="confirmRef" />
  </div>
</template>
